import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { computeDisplaySize } from './panda-view-geometry';

// Stands in for a photo that has no thumbnail and no blurhash to fall back on.
// The filename is the only thing left that lets a customer tell which photo this is.
//
// It replaces PandaView's canvas, which lays out from its intrinsic width/height. A div has
// no intrinsic size, so without the same display ratio it collapses to the height of its
// text. computeDisplaySize is the helper BlurhashCanvas uses, so all three previews agree.
const PhotoPreviewPlaceholder = ({ filename, rawDimensions, sizeArea }) => {
  const { width, height } = useMemo(
    () => computeDisplaySize(rawDimensions, sizeArea),
    [rawDimensions, sizeArea],
  );

  // Set only the longer side and let aspect-ratio derive the other. Setting both lets
  // preview-sizing's max-width and max-height clamp them independently, which squares the
  // box off rather than preserving the ratio. A canvas is immune because replaced elements
  // keep their intrinsic ratio while clamping; a div does not.
  const leadingSide =
    width >= height ? { width: `${width}px` } : { height: `${height}px` };

  return (
    <div
      data-test-id="photo-preview-placeholder"
      className="photo-preview-placeholder d-flex align-items-center justify-content-center text-center"
      style={{ ...leadingSide, aspectRatio: `${width} / ${height}` }}>
      {filename && (
        <span className="photo-preview-placeholder-filename">{filename}</span>
      )}
    </div>
  );
};

PhotoPreviewPlaceholder.propTypes = {
  filename: PropTypes.string,
  rawDimensions: PropTypes.shape({
    w: PropTypes.number,
    h: PropTypes.number,
  }),
  sizeArea: PropTypes.arrayOf(PropTypes.number),
};

PhotoPreviewPlaceholder.defaultProps = {
  filename: '',
  rawDimensions: null,
  sizeArea: null,
};

export default PhotoPreviewPlaceholder;
